import type { CategoryPrompt } from "../types";

import React from "react";

import {
  BedDouble,
  CircleDot,
  Dumbbell,
  FlaskConical,
  Layers,
  LineChart,
  Timer,
} from "lucide-react";

export const supplementsPrompts: CategoryPrompt[] = [
  {
    icon: <Dumbbell />,
    title: "Goal-First Landing Pages",
    description:
      "Create a page targeting specific goals with scientific explanations, dosing protocols, and testimonials from users with similar goals.",
    prompt: `**Build a goal-first landing page that targets specific health objectives with scientific mechanisms, precise dosing protocols, and testimonials from goal-aligned users.**

**HEALTH GOAL:** e.g., "Better Sleep Support" OR "Muscle Recovery & Performance" OR "Immune System Strengthening" OR "Cognitive Focus & Memory"

**SCIENTIFIC APPROACH:** e.g., Mechanism of action, optimal dosing, timing, synergistic ingredients

**Target Customer:** Choose one:
- [ ] Goal-focused (specific outcome desired)
- [ ] Evidence-seeking (want science-backed approach)
- [ ] Protocol-followers (need structured guidance)

**Tone & Style:**
- Clinical health advisor
- Goal-achievement focused
- Evidence-based confidence
- "Science-backed path to your goal"

**CTA Guidelines:** Each CTA should:
- Reference goal directly ("Support Better Sleep")
- Use outcome language ("Achieve Your Goal")
- Include science backing ("Research-Backed")
- Emphasize effectiveness ("Get Results")

**Conversion Tactics Use:**
- Scientific goal mechanism explained
- Optimal dosing protocols
- Clinical study results for goal
- Goal-specific testimonials
- Timeline expectations
- Stack/combination recommendations

**Key Elements to Include:**
- **Hero Section:** Goal-focused headline, outcome image, "Achieve [Goal]" CTA
- **The Goal Challenge:** Why this is difficult to achieve (2-3 paragraphs)
- **The Science of [Goal]:**
  - Biological pathways involved
  - What your body needs
  - Common deficiencies/imbalances
  - How supplementation helps
  - Mechanism of action
- **Our Goal-Targeted Formula:**
  - Primary supplement recommendation
  - Why specifically for this goal
  - Key active ingredients
  - Dosing rationale
- **Key Ingredients Breakdown (3-6):**
  - Ingredient name
  - Exact dosage in formula
  - Role in achieving goal
  - Clinical studies on ingredient for this goal
  - Optimal absorption form used
  - Synergy with other ingredients
- **Dosing Protocol:**
  - Recommended daily dose
  - When to take (morning/evening/with food)
  - Cycling if applicable
  - Loading phase if relevant
  - Maintenance dosing
  - What to avoid (interactions)
- **Clinical Evidence for Goal:**
  - Studies specific to this outcome (3-5)
  - Sample sizes and duration
  - Results percentages
  - Statistical significance
  - Timeline to effect
  - Dose-response relationship
- **Expected Timeline:**
  - Week 1-2: Initial effects
  - Week 4-6: Noticeable improvement
  - Week 8-12: Optimal results
  - Long-term maintenance
- **Goal-Specific Testimonials (8-12):**
  - Customer name + photo
  - Their specific goal
  - Baseline status
  - Dosing protocol followed
  - Timeline to results
  - Measurable improvements
  - Life quality changes
  - Still using (maintenance)
  - Quantified results when possible
- **Complementary Stack:**
  - Additional supplements enhancing goal
  - Synergistic combinations
  - Timing coordination
  - Stack discount if applicable
- **Lifestyle Optimization:**
  - Diet considerations
  - Exercise recommendations
  - Sleep habits
  - Stress management
  - How supplementation fits holistically
- **Measurement & Tracking:**
  - How to monitor progress
  - Objective markers
  - Subjective improvements to notice
  - When to adjust dosing
- **Safety Profile:**
  - Side effects (if any)
  - Drug interactions
  - Who shouldn't take
  - Pregnancy/nursing considerations
  - Medical conditions to consider
- **Expert Endorsement:**
  - Nutritionist/doctor validation
  - Professional athlete use (if performance goal)
  - Sleep specialist (if sleep goal)
  - Credentials displayed
- **Comparison: Our Formula vs. Others:**
  - Dosage adequacy
  - Form/bioavailability
  - Ingredient quality
  - Goal-specific optimization
- **Quality Assurance:**
  - Third-party testing
  - Purity verification
  - Potency guarantee
  - GMP certification
- **Money-Back Guarantee:** "Results toward goal or refund"
- **CTAs:** Hero, after science, with protocol, testimonials (5-6 total)
- **FAQ:** 7-9 questions (How long to work? Safe with medications? Dosing flexibility? Works for everyone? Clinical backing? Stack with others? Long-term use?)

**Goal:** Convert goal-oriented supplement shoppers into confident buyers by providing comprehensive scientific explanation of how your formula specifically supports their objective, backed by optimal dosing protocols, clinical evidence, and success stories from users who achieved similar goals - positioning supplementation as a scientifically-sound strategy for reaching their health outcomes.`,
  },
  {
    icon: <BedDouble />,
    title: "Symptom Listicle Advertorials",
    description:
      "Build a listicle addressing common symptoms, with each problem linking to your targeted supplements and CTAs throughout.",
    prompt: `**Build a symptom-focused listicle that identifies common health signals and positions your targeted supplements as the nutritional solutions.**

**TOPIC:** e.g., "7 Signs You're Deficient in Key Nutrients" OR "5 Symptoms Your Body Is Crying for Help" OR "Why You're Always Tired: Hidden Deficiencies"

**SUPPLEMENTS FEATURED:** e.g., "Our magnesium solves symptom #3" OR "Our B-complex addresses issue #5"

**Reader Type:** Choose one:
- [ ] Symptom-aware (experiencing but don't know cause)
- [ ] Self-diagnosers (researching their symptoms)
- [ ] Health optimizers (want to address early signs)

**Tone & Style:**
- Validating not alarmist
- Educational and empowering
- Science-backed explanations
- "Listen to your body" guidance

**CTA Guidelines:** Each CTA should:
- Reference symptom relief ("End Fatigue")
- Use nutritional language ("Support Healthy Levels")
- Include research backing ("Clinically Studied")
- Emphasize root cause ("Address Deficiency")

**Conversion Tactics Use:**
- Symptom recognition validation
- Nutritional deficiency explanations
- Clinical research on supplementation
- Symptom improvement timelines
- Multi-symptom addressing
- Doctor/nutritionist validation

**Key Elements to Include:**
- **Hook:** Relatable symptom moment people ignore (2-3 paragraphs)
- **List Items (6-9 symptoms):** Numbered symptom → deficiency cause → your supplement solution
- **Symptom Structure Per Item:**
  - Symptom headline: "#X: [Common symptom everyone experiences]"
  - What it feels like: Detailed description
  - Why this happens: Nutritional deficiency or imbalance causing it
  - Prevalence: "40% of adults experience this"
  - Biological mechanism: How deficiency creates symptom
  - Long-term risks: What happens if deficiency continues
  - The solution: Your supplement with targeted nutrients
  - Key ingredients: Specific nutrients addressing root cause
  - Dosage: Therapeutic levels explained
  - How it works: Mechanism of correction
  - Research backing: Studies on supplementation for this symptom (2-3)
  - Expected timeline: When symptom relief occurs
  - Additional benefits: Other improvements from addressing deficiency
  - Nutritionist insight: Expert quote on symptom-deficiency link
  - Customer testimonial: Someone who resolved this exact symptom
- **Product Callouts:** Nutrient highlights with "Address This" CTA
- **Multi-Symptom Connection:** How several symptoms link to one deficiency
- **Nutrient Deep Dives:** Key vitamins/minerals explained (2-3 throughout)
- **Deficiency Testing:** How to confirm through labs if desired
- **Dietary Sources:** Food sources + why supplementation helps
- **Absorption Optimization:** Forms that work best, timing, co-factors
- **Clinical Evidence:** Studies on supplementation efficacy (8-12 references)
- **Dosage Guidance:** Therapeutic vs. maintenance levels
- **Safety Profile:** Upper limits, interactions, who shouldn't supplement
- **Symptom Tracking:** How to monitor improvement
- **Complementary Approaches:** Lifestyle factors alongside supplementation
- **Doctor Consultation:** When to see healthcare provider
- **CTAs:** After symptom #2, after symptom #5, conclusion (5-7 total)
- **FAQ:** 5-7 questions (Safe daily? Works for all symptoms? How long? Side effects? Testing needed? Prescription-strength? Food vs. supplement?)

**Goal:** Convert symptom-aware shoppers into proactive buyers by helping them recognize their body's nutritional deficiency signals, explaining the scientific root causes with research backing, and demonstrating how your targeted supplements provide the specific nutrients needed to address underlying deficiencies causing their symptoms - empowering informed self-care through evidence-based supplementation.`,
  },
  {
    icon: <Layers />,
    title: "Demographic-Specific Quiz",
    description:
      "Create age and gender-specific quizzes addressing health patterns, then provide personalized supplement recommendations.",
    prompt: `**Build a demographic-specific quiz that addresses age and gender-related health patterns with personalized supplement recommendations tailored to life stage needs.**

**DEMOGRAPHIC FOCUS:** e.g., "Men's Health Over 40" OR "Women's Wellness 30-50" OR "Active Seniors 60+" OR "Women's Hormonal Health"

**HEALTH PATTERNS:** e.g., Hormonal shifts, metabolism changes, energy decline, age-specific deficiencies

**Quiz Taker Type:** Choose one:
- [ ] Life stage transitioners (experiencing age-related changes)
- [ ] Proactive health optimizers (preventing decline)
- [ ] Symptom-aware (noticing specific patterns)

**Tone & Style:**
- Age-appropriate empathy
- Life stage expertise
- Science-backed guidance
- "Your stage has unique needs"

**Quiz Question Guidelines:** Each question should:
- Address demographic-specific symptoms
- Use life stage-appropriate language
- Build toward hormonal/metabolic patterns
- Include lifestyle and health history
- 9-12 total questions maximum

**Email Gate Strategy:**
- Place after question #7 (deep commitment)
- Frame as "Get your [demographic] health plan"
- Offer incentive: "Unlock plan + 20% off [demographic] stack"
- Single field: email only
- Trust line: "Join 100,000+ [demographic] optimizing health"

**Results Page Elements:**
- **Personalized Headline:** "[Demographic] health profile: [Pattern identified] - here's your plan"
- **Life Stage Context:** What's happening biologically at this age/gender (2-3 paragraphs)
- **Pattern Identification:** Hormonal, metabolic, or deficiency patterns detected
- **Recommended Stack:** 2-5 supplements targeting demographic needs
- **Why These For Your Stage:** Age/gender-specific nutritional requirements
- **Product Details Per Supplement:**
  - What it addresses for this demographic
  - Key nutrients and dosages
  - Life stage benefits
  - How it works in your body now
  - When to take
  - "Add to Stack" CTA
- **Dosing Protocol:** Age-appropriate amounts and timing
- **Expected Benefits Timeline:** Realistic results for this demographic
- **Product Cards Include:** Image, demographic benefit, key actives, research, dosing
- **Match Reasoning:** "At [age], [gender] typically need [nutrient] because..."
- **Stack Synergy:** How supplements work together for demographic
- **Life Stage Priorities:**
  - Men 40+: Testosterone support, prostate health, heart health, muscle maintenance
  - Women 30-50: Hormone balance, bone density, energy, stress management
  - Seniors 60+: Cognitive function, joint health, immune support, vitality
  - Women hormonal: Cycle support, mood, energy, reproductive health
- **Demographic Health Science:**
  - Hormonal changes at this stage
  - Metabolic shifts
  - Nutritional absorption changes
  - Common deficiencies for demographic
  - Preventative health priorities
- **Complementary Lifestyle:**
  - Exercise recommendations for age
  - Diet adjustments for stage
  - Sleep needs
  - Stress management
- **Lab Testing Guidance:** Which biomarkers to monitor
- **Demographic Success Stories (6-10):**
  - Similar age/gender testimonials
  - Symptoms improved
  - Energy/vitality changes
  - Lab marker improvements
  - Quality of life gains
- **Clinical Evidence:** Studies on demographic-specific needs
- **Expert Validation:** Doctor/nutritionist perspective on life stage supplementation
- **Safety for Demographic:** Considerations for age/gender
- **Long-Term Health:** Prevention focus for future stages
- **When to Adjust:** Signs to modify stack as you age
- **Bundle Option:** Complete demographic stack with savings
- **Budget Tiers:** Essential vs. comprehensive for demographic
- **CTAs:** "Start Your [Demographic] Stack" and bundles
- **Retake Option:** "Life stage changed? Reassess"

**Conversion Tactics Use:**
- Age/gender validation ("This is normal for your stage")
- Peer social proof (similar demographic)
- Preventative health framing
- Hormone-focused education
- Life stage milestone targeting
- Professional demographic expertise

**Key Quiz Questions by Demographic:**
**Men 40-60:**
- Energy and vitality levels
- Muscle mass maintenance
- Exercise recovery
- Prostate concerns
- Libido changes
- Stress/performance
- Sleep quality
- Weight management
**Women 30-50:**
- Menstrual cycle changes
- Perimenopause symptoms
- Energy fluctuations
- Mood/stress patterns
- Bone health awareness
- Sleep disruptions
- Weight challenges
- Skin/hair changes
**Seniors 60+:**
- Cognitive sharpness
- Joint mobility
- Energy levels
- Immune resilience
- Bone health
- Digestive changes
- Sleep quality
- Heart health
**Universal Questions:**
- Current health priorities
- Family health history
- Medications/conditions
- Activity level for age
- Dietary patterns
- Health goals for stage

**Goal:** Convert demographic-aware shoppers into loyal customers by addressing their unique age and gender-related health patterns with targeted supplement recommendations that acknowledge biological realities of their life stage while providing science-backed solutions for their specific transitional needs - backed by peer testimonials and expert validation.`,
  },
  {
    icon: <CircleDot />,
    title: "Transparency & Lab Testing Page",
    description:
      "Build a transparency page with downloadable lab reports and certifications in plain language.",
    prompt: `**Build a transparency page showcasing lab testing, certifications, and quality standards in accessible language with downloadable documentation.**

**TRANSPARENCY FOCUS:** e.g., "Our Testing Standards" OR "Complete Transparency Promise" OR "Third-Party Verified Quality"

**CREDIBILITY ANGLE:** e.g., Independent lab testing, purity verification, potency guarantees, contaminant screening

**Visitor Type:** Choose one:
- [ ] Quality-skeptics (burnt by supplement industry)
- [ ] Research-driven (verify everything before buying)
- [ ] Health-serious (want pharmaceutical-grade standards)

**Tone & Style:**
- Radically transparent and honest
- Confident through proof not claims
- Educational without jargon overload
- "Nothing to hide" openness

**CTA Guidelines:** Each CTA should:
- Reference verification ("See The Proof")
- Use trust language ("Independently Tested")
- Include credential specifics ("Third-Party Certified")
- Emphasize quality ("Pharmaceutical-Grade")

**Conversion Tactics Use:**
- Downloadable lab reports (COAs)
- Certification badge explanations
- Comparison to industry minimums
- Batch-specific testing access
- Real-time test result database
- Manufacturing facility transparency

**Key Elements to Include:**
- **Hero Section:** Bold transparency statement, "View Our Lab Results" CTA
- **Our Quality Promise:** What transparency means to us (2-3 paragraphs)
- **Testing Standards Overview:** What we test for and why
- **Third-Party Lab Partners:**
  - Lab names with credentials
  - Accreditations (ISO 17025, etc.)
  - Independence verification
  - Testing frequency
  - Lab facility locations
- **Certificate of Analysis (COA) Access:**
  - How to find your batch number (with photo guide)
  - Searchable COA database by product/batch
  - Sample COA with annotations explaining each test
  - Download options (PDF)
  - Archive of all batches
- **What We Test For (12-18 categories):**
  - **Potency Verification:** Label claim accuracy (±5% tolerance)
  - **Heavy Metals:** Lead, mercury, arsenic, cadmium (limits shown)
  - **Microbial Contamination:** Bacteria, yeast, mold, E.coli, Salmonella
  - **Pesticides & Herbicides:** Residue screening (400+ tested)
  - **Solvents & Residuals:** Manufacturing residue testing
  - **Allergen Cross-Contamination:** Gluten, soy, dairy screening
  - **Identity Verification:** HPLC, FTIR confirming ingredients
  - **Dissolution Testing:** Capsule/tablet breakdown rates
  - **Stability Testing:** Shelf-life validation
  - **Rancidity (oils):** Peroxide value, anisidine value
  - **Fillers & Adulterants:** Screening for undeclared ingredients
  - **GMO Testing:** Non-GMO verification
- **Certification Explanations:**
  - Each badge with plain-language breakdown
  - What standards must be met
  - Who verifies (third-party body)
  - Why it matters for your health
  - Re-certification frequency
  - Verification links to certifying bodies
- **Our Testing vs. Industry:**
  | Test Category | Industry Minimum | Our Standard |
  | Batch testing | Spot checking | Every batch |
  | Heavy metals | FDA limits | California Prop 65 (stricter) |
  | Microbial | Basic screening | Comprehensive panel |
  | Potency | ±20% tolerance | ±5% tolerance |
  | Third-party | Optional | Required always |
  - **Chart spans 8-12 comparison rows**
- **Manufacturing Standards:**
  - GMP-certified facility details
  - FDA registration numbers
  - NSF/cGMP certifications
  - Facility audit results (available)
  - Clean room classifications
  - Quality control procedures
- **Sourcing Transparency:**
  - Ingredient origins by country
  - Supplier qualification process
  - Raw material testing requirements
  - Traceability documentation
  - Ethical sourcing standards
- **Real Lab Reports:** 5-8 recent COAs prominently displayed with interpretation guides
- **Batch Tracking:** How to trace your bottle from raw materials to delivery
- **Purity Guarantees:** What we guarantee and back with testing
- **Contaminant Limits:** Exact acceptable levels with safety context
- **Label Claims Substantiation:** Every claim backed by testing
- **Quality Control Process:** Step-by-step from sourcing to shipping
- **Failed Batch Policy:** What happens if batch doesn't pass (transparency on rejections)
- **Customer Access:** How to request testing documentation
- **Supplement Facts Accuracy:** Third-party verification of label
- **Stability Data:** Expiration date validation testing
- **CTAs:** Hero, after COAs, with certifications, products (5-6 total)
- **FAQ:** 8-10 questions (How to read COA? Test every batch? Third-party really? Ever fail? International standards? Access old reports? What if concerned? Contamination ever found?)

**Goal:** Convert skeptical, quality-conscious consumers into loyal customers by providing unprecedented transparency through accessible lab results, clear certification explanations, and pharmaceutical-grade standards that prove you have nothing to hide and everything to show - eliminating industry trust issues through downloadable proof and comparison that demonstrates your commitment to quality exceeds all minimums.`,
  },
  {
    icon: <FlaskConical />,
    title: "Clinical Study Results",
    description:
      "Create a study-focused page with participant demographics, control group comparison, statistical significance, and link to research.",
    prompt: `**Build a clinical study results page that showcases research credibility through detailed methodology, participant data, statistical analysis, and published research access.**

**STUDY FOCUS:** e.g., "Clinical Trial: [Product] for [Condition]" OR "Published Research on [Ingredient]" OR "Double-Blind Placebo Study Results"

**STUDY TYPE:** e.g., Randomized controlled trial, placebo-controlled, double-blind, peer-reviewed

**Visitor Type:** Choose one:
- [ ] Evidence-seekers (want proof before buying)
- [ ] Science-literate (understand research)
- [ ] Medical professionals (evaluating for patients)

**Tone & Style:**
- Scientific and rigorous
- Data-driven not marketing
- Transparent about limitations
- Confident through evidence

**CTA Guidelines:** Each CTA should:
- Reference research backing ("Clinically Proven")
- Use scientific language ("Evidence-Based")
- Include publication info ("Published Study")
- Link to tested products ("Shop Study Formula")

**Conversion Tactics Use:**
- Published research links
- Peer-review journal citations
- Visual data graphs/charts
- Statistical significance emphasis
- Control group comparisons
- Independent research facility credentials

**Key Elements to Include:**
- **Hero Section:** Study headline, key finding statistic, "Read Full Study" + "Shop Study Formula" CTAs
- **Study Overview:** Research summary and main findings (2-3 paragraphs)
- **Key Results Highlight:** Top 3-5 statistically significant findings with large percentages displayed
- **Study Design & Methodology:**
  - Study type: RCT, double-blind, placebo-controlled
  - Duration: Weeks/months of intervention
  - Sample size: Total participants (power calculation shown)
  - Randomization: Method and allocation
  - Control groups: Placebo and/or comparative treatment
  - Blinding: Who was blinded (participants, researchers, analysts)
  - Inclusion criteria: Who could participate
  - Exclusion criteria: Who was excluded and why
  - Dosage protocol: Exact amounts and timing
  - Compliance monitoring: How adherence was tracked
  - Measurement methods: Tools and frequency
  - Primary endpoints: Main outcomes measured
  - Secondary endpoints: Additional measurements
- **Participant Demographics:**
  - Total enrolled vs. completed
  - Age range and mean
  - Gender breakdown (% male/female)
  - Baseline health characteristics
  - BMI ranges
  - Geographic location
  - Ethnicity distribution
  - Dropout rate and reasons
  - Compliance rate
- **Results Data:**
  - **Primary Outcome Measures:**
    - Treatment group results
    - Control/placebo group results
    - Difference between groups
    - Statistical significance (p-values)
    - Effect size (Cohen's d or similar)
    - Confidence intervals (95% CI)
  - **Secondary Outcomes:** Same detail as above
  - **Time-Course Data:** Results at different timepoints
  - **Subgroup Analysis:** Results by demographics if applicable
- **Visual Data Presentation:**
  - Bar graphs: Treatment vs. control comparison
  - Line graphs: Improvement over time
  - Forest plots: Effect sizes
  - Box plots: Data distribution
  - Before/after comparison charts
  - Percentage improvement visuals
- **Statistical Analysis:**
  - Methods used (ANOVA, t-tests, etc.)
  - Intention-to-treat analysis
  - Per-protocol analysis
  - Adjustment for confounders
  - Missing data handling
  - Significance threshold (p<0.05)
- **Control Group Comparison:**
  | Outcome | Treatment Group | Placebo Group | P-Value | Effect Size |
  | Primary measure | 42% improvement | 8% improvement | p<0.001 | 0.82 (large) |
  | Secondary measure | 38% improvement | 12% improvement | p<0.003 | 0.67 (medium) |
  - **Chart spans 5-8 key outcomes**
- **Safety & Adverse Events:**
  - Adverse events in treatment group
  - Adverse events in placebo group
  - Serious adverse events (if any)
  - Dropout due to adverse events
  - Tolerability assessment
- **Researcher Credentials:**
  - Principal investigator bio
  - Institution affiliation
  - Research background
  - Publications history
  - Conflicts of interest disclosure
- **Research Facility:**
  - Institution name and reputation
  - Location and accreditation
  - Ethics approval (IRB)
  - Clinical trial registration number
- **Peer Review Status:**
  - Published in: [Journal name]
  - Publication date
  - Journal impact factor
  - Peer-review process completed
  - DOI and citation
  - Link to full paper
- **Study Limitations:**
  - Sample size constraints
  - Duration limitations
  - Generalizability considerations
  - Funding source disclosure
  - Author conflicts of interest
- **Product Connection:**
  - Exact formulation tested
  - Dosage used in study
  - How to purchase study formula
  - Consistency guarantee
- **Additional Supporting Research:**
  - Related studies (3-5)
  - Meta-analyses if available
  - Mechanism studies
  - Long-term follow-up data
- **Expert Commentary:**
  - Independent researcher analysis
  - Clinical perspective on findings
  - Real-world application
- **Media Coverage:** Press releases, news articles about study
- **Patient Study Photos:** Before/after if available (with consent)
- **Full Study Download:** PDF access or journal link
- **CTAs:** Hero, after results, with product connection (4-5 total)
- **FAQ:** 7-9 questions (Who funded? Conflicts of interest? Replicable? All products studied? Same dosage available? Side effects? Compared to prescription? Long-term data?)

**Goal:** Convert evidence-demanding shoppers into confident buyers by providing comprehensive clinical research transparency that proves efficacy through rigorous scientific methodology, statistically significant results, peer-reviewed publication, and independent researcher validation - eliminating skepticism through detailed data disclosure that demonstrates your supplement delivers measurable, clinically-proven benefits backed by gold-standard research.`,
  },
  {
    icon: <LineChart />,
    title: "Results Timeline Page",
    description:
      "Build a page managing expectations about adaptation periods, explaining cumulative effects, and showing customer progress tracking.",
    prompt: `**Build a results timeline page that sets realistic expectations about supplement effects while explaining cumulative benefits and showcasing customer progress tracking.**

**TIMELINE FOCUS:** e.g., "What to Expect: Your [Supplement] Journey" OR "Results Timeline: Month by Month" OR "Your Progress Guide"

**SUPPLEMENT TYPE:** e.g., Sleep support, cognitive enhancement, joint health, immune support

**Target Audience:** Choose one:
- [ ] Impatient users (want instant results)
- [ ] Realistic planners (want to know timeline)
- [ ] Commitment-hesitant (unsure if worth it)

**Tone & Style:**
- Honest and realistic
- Educational about biology
- Encouraging patience
- "Trust the process" guidance

**CTA Guidelines:** Each CTA should:
- Reference commitment ("Start Your Journey")
- Use timeline language ("See Progress")
- Include expectation-setting ("Results Take Time")
- Emphasize persistence ("Stay Consistent")

**Conversion Tactics Use:**
- Week-by-week milestone explanations
- Science of why timing matters
- Customer progress photos/journals
- Early subtle changes validation
- Cumulative effect education
- Tracking tools provided

**Key Elements to Include:**
- **Hero Section:** "Your Results Journey" headline, timeline visual, "Begin Today" CTA
- **Why Supplements Take Time:** Biological explanation (2-3 paragraphs on cellular changes)
- **Realistic Expectations Overview:**
  - Not medication (slower, gentler)
  - Cumulative benefits build
  - Individual variation exists
  - Consistency is critical
  - Patience pays off
- **Detailed Timeline by Phase:**
  **Week 1-2: Adaptation & Foundation**
  - What's happening biologically: Body recognizing nutrients, cellular uptake beginning
  - What you might feel: Subtle energy shifts, placebo effect possible, digestive adjustment
  - What you won't feel yet: Full benefits (be patient)
  - Customer experiences: "Noticed slight changes in sleep latency"
  - Science explanation: Nutrient levels rising, receptors adjusting
  - What to do: Take consistently, track baseline symptoms
  **Week 3-4: Early Changes**
  - What's happening: Cellular processes optimizing, nutrient saturation improving
  - What you might feel: First noticeable improvements, energy patterns shifting
  - Milestones: 20-30% report initial benefits
  - Customer experiences: "Sleep quality improving, falling asleep easier"
  - Science explanation: Therapeutic levels reached, biological pathways activating
  - What to do: Continue protocol, note improvements
  **Week 6-8: Visible Progress**
  - What's happening: Cumulative effects manifesting, systems rebalancing
  - What you should feel: Clear improvements, consistent benefits
  - Milestones: 60-70% report significant changes
  - Customer experiences: "Waking refreshed, consistent energy all day"
  - Science explanation: Full receptor saturation, optimal pathway function
  - What to do: Assess progress, adjust if needed
  **Week 10-12: Optimal Results**
  - What's happening: Maximum benefit realization, full adaptation
  - What you should feel: Peak effects, new baseline established
  - Milestones: 85-90% achieve primary goals
  - Customer experiences: "Life-changing difference, best I've felt in years"
  - Science explanation: Homeostasis at new optimal level
  - What to do: Continue for maintenance, celebrate progress
  **3-6 Months: Long-Term Benefits**
  - What's happening: Sustained improvements, preventative effects
  - What you'll maintain: Consistent results, health optimization
  - Additional benefits: Secondary effects emerging
  - Customer experiences: "Can't imagine going without it"
  - Science explanation: Deep cellular adaptation, systemic benefits
  - What to do: Maintain protocol, periodic assessment
- **Individual Variation Factors:**
  - Baseline deficiency level
  - Age and metabolism
  - Genetics and absorption
  - Lifestyle factors (diet, sleep, stress)
  - Health conditions
  - Other medications/supplements
- **Cumulative Effect Education:**
  - Why benefits build over time
  - Cellular turnover timelines
  - Receptor sensitivity changes
  - Nutrient saturation curves
  - Synergistic compound effects
- **Customer Progress Tracking (10-15 examples):**
  - Customer name + photo
  - Starting symptoms/baseline
  - Week-by-week journal entries
  - Milestone achievements
  - Setbacks and plateaus
  - Final results at 12 weeks
  - Measurable improvements
  - Continued use status
- **Visual Progress Graphs:**
  - Symptom severity over time
  - Energy levels tracked
  - Sleep quality scores
  - Pain reduction curves
  - Mood stability charts
- **What to Track:**
  - Primary symptoms daily
  - Energy levels (1-10 scale)
  - Sleep quality metrics
  - Mood and stress
  - Physical performance
  - Downloadable tracking sheet
- **When to Adjust:**
  - Signs protocol isn't working
  - When to increase dose
  - When to add complementary supplements
  - When to consult healthcare provider
- **Why People Quit Early:**
  - Impatience (expect instant results)
  - Not tracking (miss subtle improvements)
  - Inconsistent use (skip days)
  - Wrong expectations (unrealistic)
  - Education on persistence value
- **Comparison: Quick Fix vs. Real Solution:**
  - Medication: Fast, temporary, side effects
  - Supplements: Slower, sustainable, safe
  - Why patience = better outcomes
- **Commitment Tools:**
  - Progress tracking app/template
  - Reminder system
  - Milestone celebrations
  - Community support
  - Accountability partner suggestions
- **Money-Back Timeline:** "Try for 60 days, see results or refund"
- **Expert Perspective:** Why patience matters from nutritionist/doctor
- **CTAs:** Hero, after timeline phases, with tracking tools (5-6 total)
- **FAQ:** 7-9 questions (Not working yet? Speed up results? When give up? Individual variation? Plateau normal? Skip days impact? Long-term use safe?)

**Goal:** Convert impatient shoppers into committed users by setting realistic expectations through detailed timeline education that explains biological reasons for gradual effects, validates early subtle changes, and provides tracking tools to recognize progress - reducing early dropout through expectation management while building confidence that patience and consistency deliver superior, sustainable results backed by customer progress examples.`,
  },
  {
    icon: <Timer />,
    title: "Competitor Comparison Page",
    description:
      "Create a comparison page with side-by-side nutrition panels, ingredient quality differences, price analysis, and test results.",
    prompt: `**Build a competitor comparison page that positions your supplement as superior through transparent side-by-side analysis of formulation, quality, and value.**

**COMPARISON FOCUS:** e.g., "Us vs. [Competitor]" OR "Why We're Different" OR "The Superior Alternative to [Brand]"

**COMPARISON ANGLE:** e.g., Ingredient quality, dosage adequacy, bioavailability, testing standards, price-per-dose

**Visitor Type:** Choose one:
- [ ] Active comparers (researching between brands)
- [ ] Competitor customers (considering switch)
- [ ] Value-conscious (want best quality for price)

**Tone & Style:**
- Factual and data-driven
- Confident not combative
- Transparent about differences
- "Here's the truth" directness

**CTA Guidelines:** Each CTA should:
- Reference switching ("Upgrade Your Supplement")
- Use proof language ("See the Difference")
- Include guarantee ("Risk-Free Trial")
- Emphasize value ("Better Formula, Better Price")

**Conversion Tactics Use:**
- Side-by-side nutrition panel comparison
- Ingredient form/quality differences
- Bioavailability data
- Price-per-serving calculations
- Third-party testing comparison
- Switcher testimonials with reasons

**Key Elements to Include:**
- **Hero Section:** Bold comparison headline, "Try the Better Option" CTA
- **Why People Switch:** Top 5 reasons customers leave competitors for you
- **Side-by-Side Comparison Chart (15-20 rows):**
  | Category | Competitor | Our Formula |
  | Vitamin D form | D2 (ergocalciferol) | D3 (cholecalciferol) - 87% more effective |
  | Magnesium form | Oxide (4% absorption) | Glycinate (bioavailable, gentle) |
  | Vitamin B12 | Cyanocobalamin | Methylcobalamin (active form) |
  | Vitamin C dose | 60mg (67% DV) | 500mg (556% DV) - therapeutic |
  | Fillers | Magnesium stearate, titanium dioxide | None - clean formula |
  | Third-party testing | Not disclosed | NSF Certified, published COAs |
  | Heavy metal testing | Unknown | <California Prop 65 limits |
  | Price per serving | $1.20 | $0.95 (21% savings) |
  | Servings per container | 30 | 60 (better value) |
  | Money-back guarantee | 30 days | 60 days risk-free |
  - **Chart covers all key differentiators**
- **Ingredient Form Quality Deep Dive:**
  - Cheap forms they use vs. premium forms you use
  - Bioavailability differences with percentages
  - Absorption rates compared
  - Why form matters for efficacy
  - Cost-cutting vs. quality focus
- **Dosage Adequacy Analysis:**
  - Their under-dosed ingredients
  - Your therapeutic levels
  - RDA vs. optimal dosing
  - Clinical research on effective doses
  - "Fairy dusting" exposure (trace amounts that do nothing)
- **Bioavailability Comparison:**
  - Absorption rate data
  - Form superiority evidence
  - Chelated minerals vs. oxides
  - Active vs. inactive vitamin forms
  - Delivery technology differences
- **Nutrition Panel Side-by-Side:**
  - Actual supplement facts labels
  - Highlighted differences
  - Annotations explaining disparities
  - Serving size tricks exposed
- **Price Per Dose Analysis:**
  - Cost per serving calculation
  - Cost per therapeutic dose
  - Monthly cost comparison
  - Annual savings with your product
  - True value breakdown
- **What's NOT In Ours:**
  - Their fillers and binders listed
  - Artificial colors they use
  - Unnecessary additives
  - Allergens they include
  - Your clean label advantage
- **Third-Party Testing Comparison:**
  - Your comprehensive testing vs. their lack
  - Certifications displayed vs. missing
  - Transparency levels
  - COA availability
  - Heavy metal limits comparison
- **Manufacturing Standards:**
  - GMP certification comparison
  - Facility quality differences
  - Country of origin
  - Supply chain transparency
- **Switcher Testimonials (8-12):**
  - Former competitor customer name + photo
  - What brand they switched from
  - Why they switched (specific reasons)
  - Differences they noticed
  - Results comparison
  - Cost savings realized
  - "Never going back"
- **Common Competitor Issues:**
  - Customer complaints about their products
  - Formulation inadequacies
  - Quality control problems
  - Customer service failures
- **Clinical Backing Comparison:**
  - Your studies vs. their claims
  - Evidence levels
  - Research investment
  - Transparent data vs. marketing
- **Efficacy Comparison:**
  - Real-world results data
  - Customer satisfaction rates
  - Repeat purchase rates
  - Review ratings compared
- **Money-Back Guarantee:** "Try 60 days risk-free - if not better, full refund"
- **Sample Offer:** Low-commitment first purchase
- **Honest Acknowledgment:** If competitor has any advantage, address transparently
- **CTAs:** Hero, after comparison chart, with testimonials, guarantee section (5-6 total)
- **FAQ:** 5-7 questions (Really better? Worth premium? Results different? Guarantee terms? Safe to switch? Comparable effects?)

**Goal:** Convert competitor customers into switchers by providing transparent, factual comparison that exposes ingredient quality differences, dosage inadequacies, and bioavailability gaps while proving your superior formulation delivers better value and results backed by testing transparency and switcher testimonials - removing risk through money-back guarantee that demonstrates confidence in product superiority.`,
  },
];
